// api/patientApi.ts

import { serverApi, handleApiResponse, handlePageResponse, getAuthHeaders, queApi, criticalApi } from "@core/api/core"
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit"
import {
  PatientListResponse,
  PatientSerializer,
  PatientDetailResponse,
  PatientDetailSerializer,
  PatientAddinfoSerializer,
  PatientWSerializer,
  PatientWResponse,
  PatientCreateResponse,
  PatientFamilyMemberSerializer,
  PatientReferrerInfo,
  PatientTABaseSerializer,
  PatientExtraInfo,
  ConsentRecord,
  PatientVitalSerializer,
} from "@/types/patient"
import { CommonTResponse, CommonExsistSerializer, PaginationType } from "@/types/common"

// fetchPatient 중복 호출 추적을 위한 전역 Map
const fetchPatientCalls = new Map<string, { callId: string; startTime: number; stack: string }>()

export interface PatientListSearchFilters {
  detailedSearch?: boolean
  visitType?: "all" | "any" | "first" | "return" | "re_first" | "no_visit"
  consentFilter?: "all" | "all_agreed" | "required_agreed" | "sms_agreed" | "not_agreed"
  requiredConsent?: "all" | "checked" | "unchecked"
  optionalConsent?: "all" | "checked" | "unchecked"
  smsRejected?: "all" | "checked" | "unchecked"
  addressQuery?: string
  gender?: "all" | "male" | "female"
  ageMin?: string
  ageMax?: string
  birthFrom?: string
  birthTo?: string
  reservationFromDate?: string
  reservationToDate?: string
  reservationMemberUuid?: string
  reservationDepartmentName?: string
  insuranceCategory?: "all" | "health_all" | "health_only" | "auto" | "aid" | "near_poor" | "general"
  herbalType?: "all" | "any" | "general" | "auto" | "health"
  chunaType?: "all" | "any"
  encounterMemberUuid?: string
  encounterDepartmentUuid?: string
  noteQuery?: string
  externalNoteQuery?: string
  patientMemoQuery?: string
  diagnosisQuery?: string
  uninsuredItemName?: string
  encounterItemQuery?: string
}

export interface CurrentPatientPayload {
  encounterUuid: string
}

export interface CurrentPatient {
  patientName: string
  patientAge: number | null
  patientGender: string
  patientId: number
  patientUuid: string
  insuranceType: string | null
  doctorUuid: string
  currentDt: string
}

////////////////////////////////////////////////////////////
// 환자 검색
export const setCurrentPatient = async (payload: CurrentPatientPayload): Promise<CurrentPatient> => {
  const response = await serverApi.post(`/patient/current-patient/`, payload)
  return handleApiResponse<CurrentPatient>(response)
}

export const searchPatientNew = async (query: string): Promise<PatientSerializer[]> => {
  const requestUrl = `/patient/`
  const params = { search: query }
  const response = await serverApi.get<PatientSerializer[]>(requestUrl, { params })
  return handleApiResponse<PatientSerializer[]>(response)
}

export const getPatientPaymentBalanceInfo = async (patientUuid: string): Promise<{ patientExtraInfo?: PatientExtraInfo; displayAmount?: number }> => {
  const requestUrl = `/patient/${patientUuid}/payment-balance-info/`
  const response = await serverApi.get<
    CommonTResponse<{
      patientExtraInfo?: PatientExtraInfo
      patient_extra_info?: PatientExtraInfo
      displayAmount?: number
      display_amount?: number
    }>
  >(requestUrl)
  const result = handleApiResponse<{
    patientExtraInfo?: PatientExtraInfo
    patient_extra_info?: PatientExtraInfo
    displayAmount?: number
    display_amount?: number
  }>(response)

  return {
    patientExtraInfo: result?.patientExtraInfo ?? result?.patient_extra_info,
    displayAmount: result?.displayAmount ?? result?.display_amount,
  }
}

// 환자 기본 정보 검색 (이름, 생년월일, 주민번호, 전화번호, 주소)
export const searchPatients = async (
  query: string,
  page: number,
  page_size: number,
  fromDate?: string,
  toDate?: string,
  ordering?: string,
  consentFilter?: string,
  firstVisitOnly?: boolean,
  searchFilters?: PatientListSearchFilters
): Promise<{ results: PatientSerializer[]; pagination: PaginationType }> => {
  const params: Record<string, string | number> = {
    page,
    page_size,
  }
  const effectiveConsentFilter = searchFilters?.detailedSearch
    ? searchFilters.consentFilter && searchFilters.consentFilter !== "all"
      ? searchFilters.consentFilter
      : undefined
    : consentFilter

  if (query && query.trim()) {
    params.search = query.trim()
  }

  if (fromDate) {
    params.from_date = fromDate
  }

  if (toDate) {
    params.to_date = toDate
  }

  if (ordering) {
    params.ordering = ordering
  }

  if (effectiveConsentFilter) {
    params.consent_filter = effectiveConsentFilter
  }

  if (firstVisitOnly) {
    params.first_visit_only = "true"
  }

  if (searchFilters?.detailedSearch) {
    params.detailed_search = "true"
  }

  if (searchFilters?.visitType && searchFilters.visitType !== "all") {
    params.visit_type = searchFilters.visitType
  }

  if (searchFilters?.requiredConsent && searchFilters.requiredConsent !== "all") {
    params.required_consent = searchFilters.requiredConsent
  }

  if (searchFilters?.optionalConsent && searchFilters.optionalConsent !== "all") {
    params.optional_consent = searchFilters.optionalConsent
  }

  if (searchFilters?.smsRejected && searchFilters.smsRejected !== "all") {
    params.sms_rejected = searchFilters.smsRejected
  }

  if (searchFilters?.gender && searchFilters.gender !== "all") {
    params.gender = searchFilters.gender
  }

  if (searchFilters?.addressQuery?.trim()) {
    params.address_query = searchFilters.addressQuery.trim()
  }

  if (searchFilters?.ageMin?.trim()) {
    params.age_min = searchFilters.ageMin.trim()
  }

  if (searchFilters?.ageMax?.trim()) {
    params.age_max = searchFilters.ageMax.trim()
  }

  if (searchFilters?.birthFrom?.trim()) {
    params.birth_from = searchFilters.birthFrom.trim()
  }

  if (searchFilters?.birthTo?.trim()) {
    params.birth_to = searchFilters.birthTo.trim()
  }

  if (searchFilters?.reservationFromDate?.trim()) {
    params.reservation_from_date = searchFilters.reservationFromDate.trim()
  }

  if (searchFilters?.reservationToDate?.trim()) {
    params.reservation_to_date = searchFilters.reservationToDate.trim()
  }

  if (searchFilters?.reservationMemberUuid?.trim()) {
    params.reservation_member_uuid = searchFilters.reservationMemberUuid.trim()
  }

  if (searchFilters?.reservationDepartmentName?.trim()) {
    params.reservation_department_name = searchFilters.reservationDepartmentName.trim()
  }

  if (searchFilters?.insuranceCategory && searchFilters.insuranceCategory !== "all") {
    params.insurance_category = searchFilters.insuranceCategory
  }

  if (searchFilters?.herbalType && searchFilters.herbalType !== "all") {
    params.herbal_type = searchFilters.herbalType
  }

  if (searchFilters?.chunaType && searchFilters.chunaType !== "all") {
    params.chuna_type = searchFilters.chunaType
  }

  if (searchFilters?.encounterMemberUuid?.trim()) {
    params.encounter_member_uuid = searchFilters.encounterMemberUuid.trim()
  }

  if (searchFilters?.encounterDepartmentUuid?.trim()) {
    params.encounter_department_uuid = searchFilters.encounterDepartmentUuid.trim()
  }

  if (searchFilters?.noteQuery?.trim()) {
    params.note_query = searchFilters.noteQuery.trim()
  }

  if (searchFilters?.externalNoteQuery?.trim()) {
    params.external_note_query = searchFilters.externalNoteQuery.trim()
  }

  if (searchFilters?.patientMemoQuery?.trim()) {
    params.patient_memo_query = searchFilters.patientMemoQuery.trim()
  }

  if (searchFilters?.diagnosisQuery?.trim()) {
    params.diagnosis_query = searchFilters.diagnosisQuery.trim()
  }

  if (searchFilters?.uninsuredItemName?.trim()) {
    params.uninsured_item_name = searchFilters.uninsuredItemName.trim()
  }

  if (searchFilters?.encounterItemQuery?.trim()) {
    params.encounter_item_query = searchFilters.encounterItemQuery.trim()
  }

  const requestUrl = `/patient/`
  const response = await serverApi.get(requestUrl, {
    params,
  })

  return handlePageResponse<PatientSerializer>(response)
}

// 진료 노트 검색 (새로운 함수)
export const searchPatientsByNote = async (
  noteQuery: string,
  page: number,
  page_size: number,
  fromDate?: string,
  toDate?: string,
  ordering?: string,
  consentFilter?: string,
  firstVisitOnly?: boolean,
  searchFilters?: PatientListSearchFilters
): Promise<{ results: PatientSerializer[]; pagination: PaginationType }> => {
  const params: Record<string, string | number> = {
    page,
    page_size,
  }
  const effectiveConsentFilter = searchFilters?.detailedSearch
    ? searchFilters.consentFilter && searchFilters.consentFilter !== "all"
      ? searchFilters.consentFilter
      : undefined
    : consentFilter

  if (noteQuery && noteQuery.trim()) {
    params.note_search = noteQuery.trim()
  }

  if (fromDate) {
    params.from_date = fromDate
  }

  if (toDate) {
    params.to_date = toDate
  }

  if (ordering) {
    params.ordering = ordering
  }

  if (effectiveConsentFilter) {
    params.consent_filter = effectiveConsentFilter
  }

  if (firstVisitOnly) {
    params.first_visit_only = "true"
  }

  if (searchFilters?.detailedSearch) {
    params.detailed_search = "true"
  }

  if (searchFilters?.visitType && searchFilters.visitType !== "all") {
    params.visit_type = searchFilters.visitType
  }

  if (searchFilters?.requiredConsent && searchFilters.requiredConsent !== "all") {
    params.required_consent = searchFilters.requiredConsent
  }

  if (searchFilters?.optionalConsent && searchFilters.optionalConsent !== "all") {
    params.optional_consent = searchFilters.optionalConsent
  }

  if (searchFilters?.smsRejected && searchFilters.smsRejected !== "all") {
    params.sms_rejected = searchFilters.smsRejected
  }

  if (searchFilters?.gender && searchFilters.gender !== "all") {
    params.gender = searchFilters.gender
  }

  if (searchFilters?.addressQuery?.trim()) {
    params.address_query = searchFilters.addressQuery.trim()
  }

  if (searchFilters?.ageMin?.trim()) {
    params.age_min = searchFilters.ageMin.trim()
  }

  if (searchFilters?.ageMax?.trim()) {
    params.age_max = searchFilters.ageMax.trim()
  }

  if (searchFilters?.birthFrom?.trim()) {
    params.birth_from = searchFilters.birthFrom.trim()
  }

  if (searchFilters?.birthTo?.trim()) {
    params.birth_to = searchFilters.birthTo.trim()
  }

  if (searchFilters?.reservationFromDate?.trim()) {
    params.reservation_from_date = searchFilters.reservationFromDate.trim()
  }

  if (searchFilters?.reservationToDate?.trim()) {
    params.reservation_to_date = searchFilters.reservationToDate.trim()
  }

  if (searchFilters?.reservationMemberUuid?.trim()) {
    params.reservation_member_uuid = searchFilters.reservationMemberUuid.trim()
  }

  if (searchFilters?.reservationDepartmentName?.trim()) {
    params.reservation_department_name = searchFilters.reservationDepartmentName.trim()
  }

  if (searchFilters?.insuranceCategory && searchFilters.insuranceCategory !== "all") {
    params.insurance_category = searchFilters.insuranceCategory
  }

  if (searchFilters?.herbalType && searchFilters.herbalType !== "all") {
    params.herbal_type = searchFilters.herbalType
  }

  if (searchFilters?.chunaType && searchFilters.chunaType !== "all") {
    params.chuna_type = searchFilters.chunaType
  }

  if (searchFilters?.encounterMemberUuid?.trim()) {
    params.encounter_member_uuid = searchFilters.encounterMemberUuid.trim()
  }

  if (searchFilters?.encounterDepartmentUuid?.trim()) {
    params.encounter_department_uuid = searchFilters.encounterDepartmentUuid.trim()
  }

  if (searchFilters?.noteQuery?.trim()) {
    params.note_query = searchFilters.noteQuery.trim()
  }

  if (searchFilters?.externalNoteQuery?.trim()) {
    params.external_note_query = searchFilters.externalNoteQuery.trim()
  }

  if (searchFilters?.patientMemoQuery?.trim()) {
    params.patient_memo_query = searchFilters.patientMemoQuery.trim()
  }

  if (searchFilters?.diagnosisQuery?.trim()) {
    params.diagnosis_query = searchFilters.diagnosisQuery.trim()
  }

  if (searchFilters?.uninsuredItemName?.trim()) {
    params.uninsured_item_name = searchFilters.uninsuredItemName.trim()
  }

  if (searchFilters?.encounterItemQuery?.trim()) {
    params.encounter_item_query = searchFilters.encounterItemQuery.trim()
  }

  const requestUrl = `/patient/`
  const response = await serverApi.get(requestUrl, {
    params,
  })

  return handlePageResponse<PatientSerializer>(response)
}

////////////////////////////////////////////////////////////
// 환자 검색
export const getCheckIdentity = async (identity: string): Promise<CommonExsistSerializer> => {
  const requestUrl = `/patient/check_identity/${identity}/`
  const response = await serverApi.get(requestUrl)
  return handleApiResponse<CommonExsistSerializer>(response)
}

// 서버에서 환자 목록 가져오는 비동기 thunk 생성
export const fetchPatients = createAsyncThunk<PatientSerializer[], void, { rejectValue: string }>(
  "patient/fetchPatients",
  async (_, { rejectWithValue }) => {
    try {
      const requestUrl = `/patient/`
      console.log("fetchPatients 11")
      const response = await serverApi.get(requestUrl)
      console.log("fetchPatients response", response.data)
      return handleApiResponse<PatientSerializer[]>(response)
    } catch (error: any) {
      return rejectWithValue(error.message || "환자 정보 리스트 가져오기 실패")
    }
  }
)

// 환자 상세정보를 서버에서 가져오는 async thunk
export const getPatientDetail = async (payload: string | { patientUuid: string; insuranceUuid?: string }): Promise<PatientDetailSerializer> => {
  const patientUuid = typeof payload === "string" ? payload : payload.patientUuid
  const insuranceUuid = typeof payload === "string" ? undefined : payload.insuranceUuid
  const requestUrl = `/patient/${patientUuid}/`
  const apiStartTime = performance.now()

  // console.log(`🌐 [API] getPatientDetail 호출 - patientUuid: ${patientUuid}`)
  const response = await criticalApi.get(requestUrl)
  // console.log(`✅ [API] getPatientDetail 응답 - patientUuid: ${patientUuid}`, {
  //   duration: performance.now() - apiStartTime,
  //   data: response.data,
  // })

  const data = handleApiResponse<PatientDetailSerializer>(response)

  if (insuranceUuid) {
    data.insuranceUuid = insuranceUuid
  }

  return data
}

export const getPatientReferrerInfo = async (patientUuid: string): Promise<PatientReferrerInfo> => {
  const requestUrl = `/patient/${patientUuid}/referrer-info/`
  const response = await criticalApi.get(requestUrl)
  return handleApiResponse<PatientReferrerInfo>(response)
}

export const fetchPatient = createAsyncThunk<
  PatientDetailSerializer, // 성공 시 반환할 타입
  string | { patientUuid: string; insuranceUuid?: string }, // 하위 호환성 유지
  { rejectValue: string }
>("patient/fetchPatient", async (payload, { rejectWithValue }) => {
  try {
    const patientUuid = typeof payload === "string" ? payload : payload.patientUuid
    const data = await getPatientDetail(payload)

    // 호출 추적에서 제거
    fetchPatientCalls.delete(patientUuid)

    return data
  } catch (error: any) {
    // 에러 발생 시에도 호출 추적에서 제거
    const patientUuid = typeof payload === "string" ? payload : payload.patientUuid
    fetchPatientCalls.delete(patientUuid)

    return rejectWithValue(error.message || "환자 상세 정보를 불러오는 중 오류가 발생했습니다.")
  }
})

export const createPatientData = async (payload: PatientWSerializer): Promise<PatientCreateResponse> => {
  const requestUrl = `/patient/`
  const response = await serverApi.post<PatientCreateResponse>(requestUrl, payload)
  return handleApiResponse<PatientCreateResponse>(response)
}

export const updatePatientData = async (data: PatientDetailSerializer): Promise<PatientSerializer> => {
  const sanitizedData = { ...data }

  if (sanitizedData.patientReferrerId === undefined) {
    sanitizedData.patientReferrerId = null
  }

  delete sanitizedData.primaryDoctorProfileName

  if (sanitizedData.patientExtraInfo && typeof sanitizedData.patientExtraInfo === "object") {
    const extraInfo = { ...sanitizedData.patientExtraInfo }
    delete extraInfo["동의서"]
    sanitizedData.patientExtraInfo = extraInfo
  }

  const requestUrl = `/patient/${data.patientUuid}/`
  const response = await serverApi.patch<PatientWResponse>(requestUrl, sanitizedData)
  return handleApiResponse<PatientSerializer>(response)
}

export const updatePatientPrimaryDoctor = async (patientUuid: string, primaryDoctorUuid: string | null): Promise<PatientSerializer> => {
  const requestUrl = `/patient/${patientUuid}/`
  const response = await serverApi.patch<PatientWResponse>(requestUrl, {
    primaryDoctorUuid,
  })
  return handleApiResponse<PatientSerializer>(response)
}

export const updatePatientReferrer = async (
  patientUuid: string,
  payload: {
    patientReferrerName: string | null
    patientReferrerId: number | null
  }
): Promise<PatientSerializer> => {
  const requestUrl = `/patient/${patientUuid}/`
  const response = await serverApi.patch<PatientWResponse>(requestUrl, payload)
  return handleApiResponse<PatientSerializer>(response)
}

export const updatePatientAdditionalInfo = async (patientUuid: string, patientAdditionalInfo: string): Promise<PatientSerializer> => {
  const requestUrl = `/patient/${patientUuid}/`
  const response = await serverApi.patch<PatientWResponse>(requestUrl, { patientAdditionalInfo })
  return handleApiResponse<PatientSerializer>(response)
}

export const markPatientVerification = async (
  patientUuid: string
): Promise<{ patientUuid: string; patientVerification: boolean; patientVerificationDt?: string | null }> => {
  const requestUrl = `/patient/${patientUuid}/mark-verification/`
  const response = await serverApi.post(requestUrl)
  return handleApiResponse<{ patientUuid: string; patientVerification: boolean; patientVerificationDt?: string | null }>(response)
}

/**
 * 환자 기타정보(임산부 정보 등) JSON 업데이트
 * PATCH /patient/{patientUuid}/ → patient_extra_info 필드만 갱신
 */
export const updatePatientExtraInfo = async (patientUuid: string, extraInfo: PatientExtraInfo): Promise<PatientSerializer> => {
  const sanitizedExtraInfo = { ...extraInfo }
  delete sanitizedExtraInfo["동의서"]

  const requestUrl = `/patient/${patientUuid}/`
  const response = await serverApi.patch(requestUrl, { patientExtraInfo: sanitizedExtraInfo })
  return handleApiResponse<PatientSerializer>(response)
}

export interface PatientTreatmentRoomSelectedItem {
  itemId: string
  timerMinutes?: number
}

export interface PatientTreatmentRoomLayoutItem extends PatientTreatmentRoomSelectedItem {
  hidden: boolean
  name?: string
}

export interface PatientTreatmentRoomVersion {
  schemaVersion: number
  revision: number
}

export interface PatientTreatmentRoomSaveResult extends PatientTreatmentRoomVersion {
  items: PatientTreatmentRoomSelectedItem[]
}

export const updatePatientTreatmentRoomSelectedItems = async (
  patientUuid: string,
  items: PatientTreatmentRoomSelectedItem[],
  layoutItems: PatientTreatmentRoomLayoutItem[] | undefined,
  version: PatientTreatmentRoomVersion
): Promise<PatientTreatmentRoomSaveResult> => {
  const requestUrl = `/patient/${patientUuid}/treatment-room-selected-items/`
  const response = await serverApi.patch(requestUrl, { items, layoutItems, ...version })
  return handleApiResponse<PatientTreatmentRoomSaveResult>(response)
}

export const updatePatientPaymentBalance = async (
  patientUuid: string,
  amount: number
): Promise<{ paymentBalanceInfo: PatientExtraInfo["paymentBalanceInfo"]; displayAmount: number }> => {
  const requestUrl = `/patient/${patientUuid}/update-payment-balance/`
  const response = await serverApi.post(requestUrl, { amount })
  return handleApiResponse<{ paymentBalanceInfo: PatientExtraInfo["paymentBalanceInfo"]; displayAmount: number }>(response)
}

export const recalculatePatientPaymentBalance = async (
  patientUuid: string
): Promise<{ paymentBalanceInfo: PatientExtraInfo["paymentBalanceInfo"]; displayAmount: number }> => {
  const requestUrl = `/patient/${patientUuid}/recalculate-payment-balance/`
  const response = await serverApi.post(requestUrl)
  return handleApiResponse<{ paymentBalanceInfo: PatientExtraInfo["paymentBalanceInfo"]; displayAmount: number }>(response)
}

export const updatePatientConsent = async (
  patientUuid: string,
  consentData: Pick<
    PatientAddinfoSerializer,
    "patientAgreementRequired" | "patientAgreementSms" | "patientAgreementSns" | "patientSmsRejected" | "patientAgreementDt"
  >
): Promise<PatientSerializer> => {
  const requestUrl = `/patient/${patientUuid}/`
  const response = await serverApi.patch(requestUrl, consentData)
  return handleApiResponse<PatientSerializer>(response)
}

export const getPatientVitals = async (
  patientUuid: string,
  options?: {
    recordedFrom?: string
    recordedTo?: string
  }
): Promise<PatientVitalSerializer[]> => {
  const requestUrl = `/patient/vitals/`
  const response = await serverApi.get(requestUrl, {
    params: {
      patient_uuid: patientUuid,
      ...(options?.recordedFrom ? { recorded_from: options.recordedFrom } : {}),
      ...(options?.recordedTo ? { recorded_to: options.recordedTo } : {}),
    },
  })
  return handleApiResponse<PatientVitalSerializer[]>(response) || []
}

export const createPatientVital = async (payload: PatientVitalSerializer & { patientUuid: string }): Promise<PatientVitalSerializer> => {
  const requestUrl = `/patient/vitals/`
  const response = await serverApi.post(requestUrl, payload)
  return handleApiResponse<PatientVitalSerializer>(response)
}

export const updatePatientVital = async (vitalUuid: string, payload: Partial<PatientVitalSerializer>): Promise<PatientVitalSerializer> => {
  const requestUrl = `/patient/vitals/${vitalUuid}/`
  const response = await serverApi.patch(requestUrl, payload)
  return handleApiResponse<PatientVitalSerializer>(response)
}

export const deletePatientVital = async (vitalUuid: string): Promise<void> => {
  const requestUrl = `/patient/vitals/${vitalUuid}/`
  const response = await serverApi.delete(requestUrl)
  handleApiResponse<void>(response)
}

export const deletePatient = async (patientUuid: string): Promise<void> => {
  const requestUrl = `/patient/${patientUuid}/`
  const response = await serverApi.delete(requestUrl)
  handleApiResponse<void>(response)
}

export const searchPatientByName = async (patientName: string): Promise<PatientSerializer[]> => {
  const requestUrl = `/patient/?search=${encodeURIComponent(patientName)}`
  const response = await serverApi.get(requestUrl)
  return handleApiResponse<PatientSerializer[]>(response)
}

export const searchReferrerPatients = async (patientName: string, pageSize = 10): Promise<PatientSerializer[]> => {
  const requestUrl = `/patient/`
  const response = await serverApi.get(requestUrl, {
    params: {
      search: patientName,
      page: 1,
      page_size: pageSize,
    },
  })
  const { results } = handlePageResponse<PatientSerializer>(response)
  return results
}

// 동일 환자 가족 ID로 묶인 구성원 조회
export const getPatientFamilyMembers = async (patientFamilyId: number | string): Promise<PatientFamilyMemberSerializer[]> => {
  const requestUrl = `/patient/family/${patientFamilyId}/`
  const response = await serverApi.get(requestUrl)
  console.log("getPatientFamilyMembers response:", response)
  return handleApiResponse<PatientFamilyMemberSerializer[]>(response)
}

export const updatePatientFamily = async (
  patientUuid: string,
  payload: { patientFamilyId?: number | null; familyMemo?: string | null }
): Promise<PatientSerializer> => {
  const requestUrl = `/patient/${patientUuid}/`

  console.log("updatePatientFamily payload:", patientUuid, payload)
  const response = await serverApi.patch<PatientSerializer>(requestUrl, payload)
  return handleApiResponse<PatientSerializer>(response)
}

//-------------------------------------------------------
// 환자 목록
//-------------------------------------------------------
export const getPatientListApi = async (
  page: number, // 1-based 페이지 번호 (PageNumberPagination은 1부터 시작)
  pageSize: number // 한 페이지당 아이템 개수
): Promise<PatientSerializer[]> => {
  const params: Record<string, any> = {
    page,
    page_size: pageSize,
  }

  const requestUrl = "/patient/" // 끝에 슬래시만 남겨 두면 Axios가 `?`부터 붙여 줍니다.
  const response = await serverApi.get<PatientSerializer[]>(requestUrl, { params })
  return handleApiResponse<PatientSerializer[]>(response)
}

//-------------------------------------------------------
// 환자 목록 - 자보
//-------------------------------------------------------
export const getPatientTAListApi = async (
  query: string,
  page: number,
  page_size: number,
  fromDate?: string,
  toDate?: string,
  ordering?: string
): Promise<{ results: PatientTABaseSerializer[]; pagination: PaginationType }> => {
  const params: Record<string, string | number> = {
    page,
    page_size,
  }

  if (query && query.trim()) {
    params.search = query.trim()
  }

  if (fromDate) {
    params.from_date = fromDate
  }

  if (toDate) {
    params.to_date = toDate
  }

  if (ordering) {
    params.ordering = ordering
  }

  const requestUrl = `/patient/ta/`
  console.log("🌐 [API] getPatientTAListApi 호출 - params:", params)
  const response = await serverApi.get(requestUrl, {
    params,
  })
  const result = handlePageResponse<PatientTABaseSerializer>(response)
  console.log("✅ [API] getPatientTAListApi 응답:", result)
  return result
}

// 동의서 삭제 (대상 정보가 있으면 서버의 최신 목록에서 대상을 확인)
export const deleteConsent = async (
  patientUuid: string,
  index: number,
  target?: { imageUuid?: string; record: ConsentRecord }
): Promise<{ deletedIndex: number; remainingCount: number }> => {
  const response = await serverApi.post(`/patient/${patientUuid}/delete-consent/`, {
    index,
    ...(target ? { expectedImageUuid: target.imageUuid?.trim() || undefined, expectedRecord: target.record } : {}),
  })
  return handleApiResponse<{ deletedIndex: number; remainingCount: number }>(response)
}

export const addConsent = async (
  patientUuid: string,
  record: ConsentRecord
): Promise<{ createdCount: number; totalCount: number; record: ConsentRecord }> => {
  const response = await serverApi.post(`/patient/${patientUuid}/add-consent/`, { record })
  return handleApiResponse<{ createdCount: number; totalCount: number; record: ConsentRecord }>(response)
}

// 엑셀 저장 이력 기록
export const recordPatientExportHistory = async (count: number): Promise<void> => {
  await serverApi.post(`/patient/export_history/`, { count })
}

export interface PatientIdentityHoldingSummarySerializer {
  residentRegistrationCount: number
  foreignRegistrationCount: number
}

export const getPatientIdentityHoldingSummary = async (): Promise<PatientIdentityHoldingSummarySerializer> => {
  const response = await serverApi.get(`/patient/identity-holding-summary/`)
  return handleApiResponse<PatientIdentityHoldingSummarySerializer>(response)
}
